import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowRight, ArrowUpRight, Check, ChevronLeft, ChevronRight, CreditCard, Link2, Smartphone, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../SEO';
import { FeaturePreview } from './FeaturePreview';
import { HomeVacanciesSection } from './HomeVacanciesSection';
import { HomeSocialsSection } from './HomeSocialsSection';
import { homeAudiences, homeFeatureKeys, homeFeatures, orderedHomeFeatures, type HomeAudienceKey, type HomeFeatureKey } from './homeFeatures';
import './home.css';

const demoActivities = [
  { name: 'Sophie', initials: 'S', action: 'sold a design kit', amount: '€ 49', icon: CreditCard },
  { name: 'Jules', initials: 'J', action: 'created a payment link', amount: '€ 149', icon: Link2 },
  { name: 'Mila', initials: 'M', action: 'welcomed a new member', amount: '€ 29 / mo', icon: CreditCard },
  { name: 'Noah', initials: 'N', action: 'sent a quick payment link', amount: '€ 85', icon: Link2 },
];

function DemoActivity() {
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (dismissed || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setIndex((value) => (value + 1) % demoActivities.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [dismissed]);
  if (dismissed) return null;
  const activity = demoActivities[index];
  const Icon = activity.icon;
  return <aside className="ws-activity" aria-label="Illustrative activity, fictional examples">
    <div key={index} className="ws-activity-content"><span className="ws-activity-avatar">{activity.initials}<i><Icon /></i></span><div><span className="ws-activity-label">Demo activity · fictional example</span><p><strong>{activity.name}</strong> {activity.action}</p></div><strong className="ws-activity-amount">{activity.amount}</strong></div>
    <button type="button" onClick={() => setDismissed(true)} aria-label="Dismiss demo activity"><X /></button>
  </aside>;
}

export function WelcomeHome() {
  const [audience, setAudience] = useState<HomeAudienceKey>('freelancers');
  const [selectedFeature, setSelectedFeature] = useState<HomeFeatureKey>('pos');
  const [canPrevious, setCanPrevious] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const slider = useRef<HTMLDivElement>(null);
  const featureRefs = useRef<Partial<Record<HomeFeatureKey, HTMLAnchorElement | null>>>({});
  const orderedFeatures = orderedHomeFeatures(audience);
  const currentAudience = homeAudiences[audience];
  const updateSlider = () => {
    const el = slider.current;
    if (!el) return;
    setCanPrevious(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    const el = slider.current;
    if (!el) return;
    const observer = new ResizeObserver(updateSlider);
    observer.observe(el);
    updateSlider();
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    slider.current?.scrollTo({ left: 0, behavior: 'instant' });
    updateSlider();
  }, [audience]);

  const showFeature = (key: HomeFeatureKey) => {
    setSelectedFeature(key);
    const el = slider.current;
    const card = featureRefs.current[key];
    if (!el || !card) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = card.offsetLeft;
    if (left < el.scrollLeft) {
      el.scrollTo({ left, behavior: reduced ? 'instant' : 'smooth' });
    } else if (left + card.offsetWidth > el.scrollLeft + el.clientWidth) {
      el.scrollTo({ left: left + card.offsetWidth - el.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
    }
  };

  const moveSlider = (direction: number) => {
    const el = slider.current;
    if (!el) return;
    const first = el.firstElementChild as HTMLElement | null;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: direction * ((first?.offsetWidth ?? 280) + 18), behavior: reduced ? 'instant' : 'smooth' });
  };

  return <div className="ws-home">
    <SEO title="Wersee — A place for everything you’re building" description="Sell, plan, connect and create with Wersee. Discover digital POS, calendars, paid communities, AI, your own cloud and mobile tools in one workspace." url="/" jsonLd={{ '@context': 'https://schema.org', '@type': 'WebSite', name: 'Wersee', url: 'https://www.wersee.com/' }} />
    <section className="ws-hero ws-wrap">
      <div className="ws-eyebrow"><span /> YOUR WORK. YOUR WORLD.</div>
      <h1>Welcome to <span>Wersee<span className="ws-title-dot">.</span></span></h1>
      <p className="ws-hero-description">A place for everything you’re building.<br className="ws-desktop-break" /> Sell, plan, connect. Make it yours.</p>
      <div className="ws-hero-actions"><Link to="/auth" className="ws-button ws-button--primary">Get started <ArrowUpRight /></Link><Link to="/signin" className="ws-signin">Already here? <strong>Sign in <ArrowRight /></strong></Link></div>
      <a className="ws-explore" href="#features">A little look around <ArrowDown /></a>
      <span className="ws-hero-coordinate ws-hero-coordinate--left">ONE WORKSPACE.<br />MANY POSSIBILITIES.</span>
      <span className="ws-hero-coordinate ws-hero-coordinate--right">MADE FOR YOUR<br />NEXT CHAPTER.</span>
    </section>

    <section id="features" className="ws-features ws-wrap" aria-labelledby="ws-features-heading">
      <div className="ws-feature-toolbar"><div><span className="ws-section-kicker">THE TOOLS, WITH YOU IN MIND</span><h2 id="ws-features-heading">Find your way to Wersee.</h2></div><div className="ws-audiences" role="group" aria-label="Choose who you are building for">{(Object.keys(homeAudiences) as HomeAudienceKey[]).map((key) => { const item = homeAudiences[key]; const Icon = item.icon; return <button key={key} type="button" aria-pressed={key === audience} onClick={() => { setAudience(key); setSelectedFeature(homeAudiences[key].features[0]); }}><Icon /><span>{item.label}</span></button>; })}</div></div>
      <div className="ws-slider" ref={slider} onScroll={updateSlider} aria-label={`Features recommended for ${currentAudience.label}`} tabIndex={0}>
        {orderedFeatures.map((key, i) => { const feature = homeFeatures[key]; return <Link key={key} to={feature.href} ref={(el) => { featureRefs.current[key] = el; }} className={`ws-feature-card ${selectedFeature === key ? 'ws-feature-card--selected' : ''}`} aria-label={`${feature.title}: ${feature.description}`}>
          <div className="ws-feature-art"><FeaturePreview feature={key} /><span className="ws-preview-number">0{i + 1}</span><span className="ws-feature-open"><ArrowUpRight /></span><span className="ws-preview-caption">PRODUCT PREVIEW</span></div>
          <div className="ws-feature-card-heading"><h3>{feature.title}</h3><feature.icon /></div><p>{feature.subtitle}</p>
        </Link>; })}
      </div>
      <div className="ws-slider-footer"><p><span className="ws-tiny-dot" /> Selected for <strong>{currentAudience.label.toLowerCase()}</strong><span className="ws-swipe-hint"> · Swipe to explore</span></p><div className="ws-slider-controls"><span>Explore all 7</span><button type="button" aria-label="Previous features" disabled={!canPrevious} onClick={() => moveSlider(-1)}><ChevronLeft /></button><button type="button" aria-label="Next features" disabled={!canNext} onClick={() => moveSlider(1)}><ChevronRight /></button></div></div>
      <div className="ws-feature-pills" role="group" aria-label="Explore a Wersee feature">{homeFeatureKeys.map((key) => { const Icon = homeFeatures[key].icon; return <button key={key} type="button" aria-pressed={selectedFeature === key} onClick={() => showFeature(key)}><Icon />{homeFeatures[key].pill}</button>; })}</div>
      <div className="ws-feature-detail"><span className="ws-detail-icon">{(() => { const Icon = homeFeatures[selectedFeature].icon; return <Icon />; })()}</span><p><strong>{homeFeatures[selectedFeature].title}.</strong> {homeFeatures[selectedFeature].description}</p><Link to={homeFeatures[selectedFeature].href}>{homeFeatures[selectedFeature].action} <ArrowUpRight /></Link></div>
    </section>

    <HomeVacanciesSection />

    <section className="ws-mobile-section ws-wrap" aria-labelledby="ws-mobile-heading">
      <div className="ws-mobile-art">
        <div className="ws-mobile-orbit" />
        <FeaturePreview feature="mobile" large />
      </div>
      <div className="ws-mobile-copy">
        <h2 id="ws-mobile-heading">Life moves.<br />Wersee comes along.</h2>
        <p>A workspace in your pocket. Accept contactless card payments on your phone, chat with customers and keep your products close wherever you go.</p>
        <div className="ws-mobile-buddy">
          <span className="ws-buddy-mark"><homeFeatures.buddy.icon /></span>
          <div>
            <strong>Say hello to your Buddy.</strong>
            <p>Your mobile AI companion, with a role you choose.</p>
          </div>
        </div>
        <Link to="/download" className="ws-button ws-button--primary">Explore the mobile app <ArrowUpRight /></Link>
      </div>
    </section>

    <HomeSocialsSection />

    <section className="ws-final-cta ws-wrap"><span className="ws-section-kicker">YOUR NEXT CHAPTER STARTS HERE.</span><h2>Good things take a start<span>.</span></h2><p>Give your work, your people and your ideas a place to grow.</p><div><Link to="/auth" className="ws-button ws-button--primary">Make Wersee yours <ArrowUpRight /></Link><Link to="/signin" className="ws-text-link">Sign in <ArrowRight /></Link></div><span className="ws-cta-wordmark" aria-hidden="true">wersee</span></section>
    <DemoActivity />
  </div>;
}
